import carrierLogo1 from 'src/assets/images/companies/img-1.png';
import carrierLogo2 from 'src/assets/images/companies/img-2.png';
import carrierLogo3 from 'src/assets/images/companies/img-3.png';
import carrierLogo4 from 'src/assets/images/companies/img-4.png';
import carrierLogo5 from 'src/assets/images/companies/img-5.png';
import carrierLogo6 from 'src/assets/images/companies/img-6.png';
import carrierLogo7 from 'src/assets/images/companies/img-7.png';
import carrierLogo8 from 'src/assets/images/companies/img-8.png';

import { buildDemoFreights } from '../../../data/buildDemoFreights';
import {
    addDemoDays,
    addDemoMinutes,
    DEMO_VERIFICATION_ANALYSTS,
    formatDemoDateTime,
    formatDemoIsoDate,
} from '../../../data/mockDemoDates';
import { getEuropeanCarrierById } from '../../../data/europeanCarriers';
import { BidVerificationStages } from '../constants';

const cityCoordinates = {
    Hamburg: [53.5511, 9.9937],
    Milan: [45.4642, 9.1900],
    Paris: [48.8566, 2.3522],
    Munich: [48.1351, 11.5820],
    Rotterdam: [51.9244, 4.4777],
    Warsaw: [52.2297, 21.0122],
    Barcelona: [41.3874, 2.1686],
    Lyon: [45.7640, 4.8357],
    Vienna: [48.2082, 16.3738],
    Antwerp: [51.2194, 4.4025],
    Berlin: [52.5200, 13.4050],
    Frankfurt: [50.1109, 8.6821],
    Rome: [41.9028, 12.4964],
    Stockholm: [59.3293, 18.0686],
    Copenhagen: [55.6761, 12.5683],
};

const createVerificationProgress = (states) => (
    BidVerificationStages.map((stage, index) => ({
        stage,
        state: states[index],
    }))
);

const createBid = ({
    id,
    carrierId,
    ...bid
}) => {
    const carrier = getEuropeanCarrierById(carrierId);

    return {
        id,
        carrierId,
        carrier: carrier.name,
        carrierCountry: carrier.country,
        headquarters: carrier.headquarters,
        website: carrier.website,
        roadCargoFocus: carrier.roadCargoFocus,
        ...bid,
    };
};

const bidTemplates = [
    createBid({
        id: 1,
        carrierId: 1,
        amount: 4900,
        status: 'Verified',
        deliveryDays: 2,
        logo: carrierLogo1,
        verificationProgress: createVerificationProgress([
            'completed', 'completed', 'completed', 'completed', 'completed',
        ]),
    }),
    createBid({
        id: 2,
        carrierId: 3,
        amount: 4850,
        status: 'Rejected',
        deliveryDays: 2,
        logo: carrierLogo2,
        verificationProgress: createVerificationProgress([
            'completed', 'completed', 'issues', 'not_completed', 'not_completed',
        ]),
    }),
    createBid({
        id: 3,
        carrierId: 4,
        amount: 4750,
        status: 'Action Required',
        deliveryDays: 2,
        logo: carrierLogo3,
        verificationProgress: createVerificationProgress([
            'completed', 'completed', 'in_progress', 'not_completed', 'not_completed',
        ]),
    }),
    createBid({
        id: 4,
        carrierId: 5,
        amount: 5100,
        status: 'Verified',
        deliveryDays: 3,
        logo: carrierLogo4,
        verificationProgress: createVerificationProgress([
            'completed', 'completed', 'completed', 'completed', 'completed',
        ]),
    }),
    createBid({
        id: 5,
        carrierId: 6,
        amount: 5300,
        status: 'Verifying',
        deliveryDays: 3,
        logo: carrierLogo5,
        verificationProgress: createVerificationProgress([
            'completed', 'not_completed', 'not_completed', 'not_completed', 'not_completed',
        ]),
    }),
    createBid({
        id: 6,
        carrierId: 7,
        amount: 4500,
        status: 'Rejected',
        deliveryDays: 3,
        logo: carrierLogo6,
        verificationProgress: createVerificationProgress([
            'completed', 'completed', 'issues', 'not_completed', 'not_completed',
        ]),
    }),
    createBid({
        id: 7,
        carrierId: 8,
        amount: 5200,
        status: 'Verifying',
        deliveryDays: 2,
        logo: carrierLogo7,
        verificationProgress: createVerificationProgress([
            'completed', 'completed', 'completed', 'in_progress', 'not_completed',
        ]),
    }),
    createBid({
        id: 8,
        carrierId: 9,
        amount: 4980,
        status: 'Action Required',
        deliveryDays: 3,
        logo: carrierLogo8,
        verificationProgress: createVerificationProgress([
            'completed', 'completed', 'not_completed', 'not_completed', 'not_completed',
        ]),
    }),
];

const documents = [
    { id: 1, title: 'Freight Specification', type: 'PDF', size: '2.4 MB' },
    { id: 2, title: 'Cargo Details', type: 'PDF', size: '1.1 MB' },
    { id: 3, title: 'Special Requirements', type: 'PDF', size: '0.8 MB' },
];

const buildFreightCreatedDate = (createdAtIso) => {
    const [year, month, day] = createdAtIso.split('-').map(Number);
    return new Date(year, month - 1, day, 9, 12, 0, 0);
};

const buildDemoTimeline = (freight) => {
    const freightCreated = buildFreightCreatedDate(freight.createdAt);

    return [
        {
            id: 1,
            title: 'Freight created',
            date: formatDemoDateTime(freightCreated),
            detail: 'by Sarah',
            completed: true,
        },
        {
            id: 2,
            title: 'Bidding opened',
            date: formatDemoDateTime(addDemoMinutes(freightCreated, 3)),
            completed: true,
        },
        {
            id: 3,
            title: `${freight.bids} bids received`,
            date: formatDemoDateTime(addDemoMinutes(freightCreated, 111)),
            completed: true,
        },
        {
            id: 4,
            title: 'Verification started',
            date: formatDemoDateTime(addDemoMinutes(freightCreated, 113)),
            detail: 'AI verification in progress',
            completed: true,
        },
        {
            id: 5,
            title: 'Bidding closes',
            date: formatDemoDateTime(addDemoMinutes(freightCreated, 168)),
            completed: false,
        },
    ];
};

const buildDemoBids = (freight) => {
    const freightCreated = buildFreightCreatedDate(freight.createdAt);

    return bidTemplates.map((bidTemplate, index) => {
        const analyst = DEMO_VERIFICATION_ANALYSTS[index % DEMO_VERIFICATION_ANALYSTS.length];
        const createdAtDate = addDemoMinutes(freightCreated, 15 + (index * 14));

        return createBid({
            ...bidTemplate,
            createdAt: formatDemoIsoDate(createdAtDate),
            submittedAt: formatDemoDateTime(createdAtDate),
            assignedAnalyst: analyst,
        });
    });
};

class CarrierGuardFreightDetailsService {
    static getInstance() {
        return new CarrierGuardFreightDetailsService();
    }

    async fetchDetails(freightId) {
        const freight = buildDemoFreights().find((item) => (
            String(item.id) === String(freightId) || item.reference === freightId
        ));

        if (!freight) return null;

        const freightCreated = buildFreightCreatedDate(freight.createdAt);
        const bids = buildDemoBids(freight);
        const timeline = buildDemoTimeline(freight);
        const pickupDate = formatDemoIsoDate(addDemoDays(freightCreated, 2));
        const deliveryDate = formatDemoIsoDate(addDemoDays(freightCreated, 4));

        return {
            ...freight,
            transportType: freight.cargo.loadType === 'FTL' ? 'Full Truckload (FTL)' : 'Less Than Truckload (LTL)',
            pickupDate,
            deliveryDate,
            pickupLocation: `${freight.origin.city}, ${freight.origin.country}`,
            deliveryLocation: `${freight.destination.city}, ${freight.destination.country}`,
            specialRequirements: '—',
            route: {
                origin: {
                    ...freight.origin,
                    coordinates: cityCoordinates[freight.origin.city],
                },
                destination: {
                    ...freight.destination,
                    coordinates: cityCoordinates[freight.destination.city],
                },
            },
            verificationOverview: {
                total: freight.bids,
                verified: freight.verification.verified,
                actionRequired: freight.verification.review,
                rejected: freight.verification.rejected,
                estimatedCompletion: '7 min',
            },
            bids,
            documents,
            timeline,
            notes: [
                {
                    id: 1,
                    author: 'Sarah',
                    initials: 'SK',
                    date: formatDemoDateTime(addDemoMinutes(freightCreated, 3)),
                    content: 'Freight created and opened for bids.',
                },
            ],
        };
    }
}

export default CarrierGuardFreightDetailsService;
